/**
 * Cache of "which person owns this expertise domain", shared through
 * `useModel('domainContext')`.
 *
 * Answer cards render a domain chip per citation, so the same handful of domain
 * ids gets looked up over and over as the user scrolls. The queue-plus-Map
 * pattern here (mirrored in `candidateContext` and `influencerContext`) collapses
 * that into one request per id for the lifetime of the model instance.
 */
import { useRef, useState } from 'react';
import { getInfluencerIdsByDomainIds } from '@/services/rest/api';

export default () => {
  // Serialising queue: every call chains onto the previous one so that two
  // components mounting at the same time cannot both miss the cache and issue
  // duplicate requests for the same domain ids.
  const queueRef = useRef(Promise.resolve());
  // domainId -> influencerId. A ref, not state, because a cache hit must not
  // re-render every consumer.
  const mapRef = useRef(new Map<string, string>());

  /**
   * Resolve domain ids to the people who own them, serving what is already
   * cached and fetching only the rest.
   *
   * Note the shape: results are delivered through `success`/`failed` callbacks
   * rather than being returned, because the caller renders them into a list it
   * already owns. The returned promise is the queue tail, useful only for
   * sequencing.
   */
  const doGetInfluencerIdsByIds = (
    domainIds: string[],
    success: (list: { id: string; influencerId: string }[]) => void,
    failed: () => void,
  ) => {
    queueRef.current = queueRef.current.then(async () => {
      const datas: { id: string; influencerId: string }[] = [];
      const notFoundIds = new Set<string>();

      domainIds.forEach((id) => {
        const influencerId = mapRef.current.get(id);
        if (influencerId) {
          datas.push({ id, influencerId });
        } else {
          notFoundIds.add(id);
        }
      });

      // Fetching only when something is missing means an all-hit call resolves
      // without touching the network - but it also means `success` is not
      // invoked at all in that case. Callers rely on their existing state
      // staying put.
      if (notFoundIds.size > 0) {
        try {
          const msg = await getInfluencerIdsByDomainIds([...notFoundIds], {
            skipErrorHandler: true,
          });
          if (msg && msg.success) {
            msg.data?.forEach((v) => {
              datas.push(v);
              mapRef.current.set(v.id, v.influencerId);
            });
          }
          success(datas);
        } catch (error) {
          failed();
        }
      }
    });
    return queueRef.current;
  };

  return {
    doGetInfluencerIdsByIds,
  };
};
